import { t } from "@lingui/macro";
import { Monitor, Moon, Sun } from "@phosphor-icons/react";
import { useTheme } from "@stylingcv/hooks";
import { cn } from "@stylingcv/utils";

type ThemeChoice = "light" | "system" | "dark";

type Props = {
  /** Extra classes on the pill container */
  className?: string;
  /** Icon size (px) */
  iconSize?: number;
  /** Use when placed on dark navy backgrounds (e.g. builder sidebar) */
  variant?: "default" | "onDark";
};

export const ThemeSwitch = ({ className, iconSize = 18, variant = "default" }: Props) => {
  const { theme, setTheme } = useTheme();

  const options: { value: ThemeChoice; Icon: typeof Sun; label: string }[] = [
    { value: "light", Icon: Sun, label: t`Light mode` },
    { value: "system", Icon: Monitor, label: t`System theme` },
    { value: "dark", Icon: Moon, label: t`Dark mode` },
  ];

  return (
    <div
      role="radiogroup"
      aria-label={t`Theme`}
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full border p-1 shadow-sm",
        variant === "onDark"
          ? "border-white/20 bg-white/5"
          : "border-border/80 bg-background",
        className,
      )}
    >
      {options.map(({ value, Icon, label }) => {
        const selected = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={label}
            onClick={() => setTheme(value)}
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-full transition-colors",
              variant === "onDark"
                ? cn(
                    "text-white/55 hover:text-white/90",
                    selected && "bg-white/20 text-white",
                  )
                : cn(
                    "text-foreground/60 hover:text-foreground",
                    selected && "bg-muted text-foreground dark:bg-white/15 dark:text-white",
                  ),
            )}
          >
            <Icon size={iconSize} weight="regular" className="shrink-0" />
          </button>
        );
      })}
    </div>
  );
};
